import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { ApplicationService } from "../../services/ApplicationService";
import { AgentActionService } from "../../services/AgentActionService";
import { Application } from "../../model_gen/application";
import {
  AgentAction,
  Agent,
  AgentActionType,
} from "../../model_gen/agent_base";
import { AgentService } from "../../services/AgentService";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { ArrowLeftIcon, PlusIcon } from "lucide-react";
import AgentActionAPIEdit from "./AgentActionAPIEdit";
import AgentActionLLMEdit, {
  AgentActionLLMEditRef,
} from "./AgentActionLLMEdit";
import AgentActionWebhookEdit from "./AgentActionWebhookEdit";
import AgentActionWorkflowEdit from "./AgentActionWorkflowEdit";
import {
  Card as UICard,
  CardHeader as UICardHeader,
  CardTitle as UICardTitle,
} from "../ui/card";
import { Tooltip } from "../ui/tooltip";
import { Info } from "lucide-react";
import { InferenceLocations } from "../../types/IConfig";
import { ActionReference } from "../../types/AgentAction";

const ApplicationActionNew: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { apiKey } = usePermissions();

  const [agent, setAgent] = useState<Agent | null>(null);
  const [application, setApplication] = useState<Application | null>(null);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Ref to access LLM edit component methods
  const llmEditRef = useRef<AgentActionLLMEditRef>(null);

  // Form state
  const initialActionType = (searchParams.get("type") as AgentActionType) || "";
  const [actionType, setActionType] = useState<AgentActionType | "">(
    initialActionType
  );
  const [actionName, setActionName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [actionConfig, setActionConfig] = useState<any>(
    // Set default config based on action type
    initialActionType === "llm_call"
      ? {
          model_config: {
            model: "gpt-4o",
            temperature: 0.7,
            max_tokens: null,
            inference_location: InferenceLocations.SERVER,
            evaluation_enabled: false,
            apply_corrections: false,
            safe_mode: false,
            streaming: false,
            logprobs: false,
          },
          prompt: "",
          templateVariables: {},
        }
      : initialActionType === "webhook_creation"
      ? {
          timeout_ms: 600000,
        }
      : initialActionType === "api_call"
      ? {
          response_type: "real",
        }
      : initialActionType === "maitai_workflow"
      ? {}
      : {}
  );
  const [whenToUse, setWhenToUse] = useState<string>("");
  const [whenNotToUse, setWhenNotToUse] = useState<string>("");
  const [references, setReferences] = useState<ActionReference[]>([]);
  const [workflowError, setWorkflowError] = useState<string>("");

  // Store configs for different action types to preserve them when switching
  const [savedConfigs, setSavedConfigs] = useState<Record<string, any>>({});

  // Save config whenever it changes
  useEffect(() => {
    if (actionType && actionConfig) {
      setSavedConfigs((prev) => ({
        ...prev,
        [actionType]: actionConfig,
      }));
    }
  }, [actionConfig, actionType]);

  const [invocationMode, setInvocationMode] = useState<
    "foreground" | "background"
  >("foreground");

  // Automatically set invocation mode to foreground when action type is webhook_creation
  useEffect(() => {
    if (actionType === "webhook_creation" && invocationMode !== "foreground") {
      setInvocationMode("foreground");
    }
  }, [actionType, invocationMode]);

  // Load agent and application details
  useEffect(() => {
    if (agentId && apiKey) {
      setLoading(true);
      AgentService.getAgent(Number(agentId), () => {}, apiKey).subscribe(
        (agentData: Agent) => {
          setAgent(agentData);

          // Load application details from the agent
          if (agentData.application_id) {
            ApplicationService.getApplication(
              agentData.application_id,
              () => {},
              apiKey
            ).subscribe(
              (app: Application) => {
                setApplication(app);
                setLoading(false);
              },
              (error: any) => {
                console.error("Error loading application:", error);
                setLoading(false);
              }
            );
          } else {
            setLoading(false);
          }
        },
        (error: any) => {
          console.error("Error loading agent:", error);
          setLoading(false);
        }
      );
    }
  }, [agentId, apiKey]);

  const handleSubmit = async () => {
    if (!actionType || !actionName || !description || !agentId) return;

    setWorkflowError("");
    if (actionType === "maitai_workflow") {
      const ref = actionConfig?.workflow_ref || actionConfig?.workflow_ref_name;
      if (!ref || !String(ref).trim()) {
        setWorkflowError("Please select or enter a workflow.");
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // If this is an LLM action, save any unsaved configuration changes first
      if (actionType === "llm_call" && llmEditRef.current) {
        const hasUnsaved = llmEditRef.current.hasUnsavedChanges();

        if (hasUnsaved) {
          await llmEditRef.current.saveUnsavedChanges();
        }
      }

      // Get the current config directly from the LLM edit component
      let finalActionConfig = actionConfig;
      if (actionType === "llm_call" && llmEditRef.current) {
        finalActionConfig = llmEditRef.current.getCurrentConfig();
      }

    const newAction: Partial<AgentAction> = {
      action_type: actionType as AgentActionType,
      action_name: actionName,
      description: description,
      agent_id: Number(agentId),
      action_config: finalActionConfig,
      status: "ENABLED",
      is_default: false,
      action_result_type: "gather_info",
      // @ts-ignore include pending model update
      invocation_mode: invocationMode,
      meta: {
        ...(whenToUse?.trim() ? { when_to_use: whenToUse.trim() } : {}),
        ...(whenNotToUse?.trim() ? { when_not_to_use: whenNotToUse.trim() } : {}),
        ...(references.length > 0 ? { references: references } : {}),
      },
    };

      AgentActionService.createAgentAction(
        Number(agentId),
        newAction,
        () => {},
        apiKey
      ).subscribe(
        (action: AgentAction) => {
          // Navigate back to agent overview
          navigate(`/agent/${agentId}`);
        },
        (error: any) => {
          console.error("Error creating action:", error);
          setIsSubmitting(false);
        }
      );
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/agent/${agentId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-8 py-8 space-y-8">
          {/* Header Card Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="h-4 bg-muted rounded w-32 animate-pulse mb-6" />
              <div className="space-y-2">
                <div className="h-10 bg-muted rounded w-64 animate-pulse" />
                <div className="h-5 bg-muted rounded w-96 animate-pulse" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-8 py-8 space-y-8">
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button
                    onClick={() => navigate("/applications")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg">
                    Applications
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button
                    onClick={() =>
                      application?.id
                        ? navigate(`/application/${application.id}`)
                        : undefined
                    }
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg">
                    {application?.application_name || "Application"}
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button
                    onClick={() => navigate(`/agent/${agentId}`)}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg">
                    {agent?.agent_name || "Agent"}
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">
                    New Action
                  </span>
                </li>
              </ol>
            </nav>

            <div className="flex items-center gap-4">
              <Button
                onClick={handleCancel}
                variant="ghost"
                size="sm"
                className="gap-2">
                <ArrowLeftIcon className="w-4 h-4" />
                Back
              </Button>
              <div className="space-y-1">
                <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                  Create New Action
                </h1>
                <p className="text-lg text-muted-foreground font-gc-reg">
                  Add a new action to {agent?.agent_name}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Unified Action Configuration Card */}
        <Card className="border-border">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-gc-med text-foreground">
              Action Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Action Details Section */}
            <div className="space-y-6">
              {/* First row: Action Name and Action Type */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Action Name */}
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">
                    Action Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={actionName}
                    onChange={(e) => setActionName(e.target.value)}
                    placeholder="e.g., send_notification, get_user_data"
                    className="font-mono"
                  />
                </div>

                {/* Action Type */}
                <div className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">
                    Action Type <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={actionType}
                    onValueChange={(value: string) => {
                      // Save current config before switching
                      if (actionType) {
                        setSavedConfigs((prev) => ({
                          ...prev,
                          [actionType]: actionConfig,
                        }));
                      }

                      setActionType(value as AgentActionType);

                      // Auto-set invocation mode for webhook actions
                      if (value === "webhook_creation") {
                        setInvocationMode("foreground");
                      }

                      // Restore saved config or set default
                      if (savedConfigs[value]) {
                        setActionConfig(savedConfigs[value]);
                      } else if (value === "llm_call") {
                        setActionConfig({
                          model_config: {
                            model: "gpt-4o",
                            temperature: 0.7,
                            max_tokens: null,
                            inference_location: InferenceLocations.SERVER,
                            evaluation_enabled: false,
                            apply_corrections: false,
                            safe_mode: false,
                            streaming: false,
                            logprobs: false,
                          },
                          prompt: "",
                          templateVariables: {},
                        });
                      } else if (value === "api_call") {
                        setActionConfig({
                          response_type: "real",
                        });
                      } else if (value === "webhook_creation") {
                        setActionConfig({
                          timeout_ms: 600000,
                        });
                      } else if (value === "maitai_workflow") {
                        setActionConfig({});
                      } else {
                        setActionConfig({});
                      }
                    }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select action type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="api_call">
                        <div className="flex flex-col">
                          <span className="font-gc-med">API Call</span>
                          <span className="text-xs text-muted-foreground">
                            External service integration
                          </span>
                        </div>
                      </SelectItem>
                      <SelectItem value="llm_call">
                        <div className="flex flex-col">
                          <span className="font-gc-med">LLM Call</span>
                          <span className="text-xs text-muted-foreground">
                            AI reasoning and text generation
                          </span>
                        </div>
                      </SelectItem>
                      <SelectItem value="webhook_creation">
                        <div className="flex flex-col">
                          <span className="font-gc-med">Webhook Creation</span>
                          <span className="text-xs text-muted-foreground">
                            Create webhook for async callbacks
                          </span>
                        </div>
                      </SelectItem>
                      <SelectItem value="maitai_workflow">
                        <div className="flex flex-col">
                          <span className="font-gc-med">Maitai Workflow</span>
                          <span className="text-xs text-muted-foreground">
                            Run a multi-step workflow
                          </span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Second row: Invocation Mode and Response Type (API only) */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-gc-med text-foreground">
                      Invocation Mode
                    </Label>
                    <Tooltip
                      content={
                        actionType === "webhook_creation"
                          ? "Webhook actions must run in foreground mode to support orchestration and execution pausing"
                          : "Determines how the action output is handled - foreground actions show results to users, background actions update context only"
                      }>
                      <Info className="w-4 h-4 text-muted-foreground" />
                    </Tooltip>
                  </div>
                  <Select
                    value={actionType === "webhook_creation" ? "foreground" : invocationMode}
                    onValueChange={(value: any) => setInvocationMode(value)}
                    disabled={actionType === "webhook_creation"}>
                    <SelectTrigger
                      className={
                        actionType === "webhook_creation"
                          ? "opacity-60 cursor-not-allowed"
                          : ""
                      }>
                      <SelectValue placeholder="Select invocation mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="foreground">
                        <div className="flex flex-col">
                          <span className="font-gc-med">Foreground</span>
                          <span className="text-xs text-muted-foreground">
                            User-facing output
                          </span>
                        </div>
                      </SelectItem>
                      {actionType !== "webhook_creation" && (
                        <SelectItem value="background">
                          <div className="flex flex-col">
                            <span className="font-gc-med">Background</span>
                            <span className="text-xs text-muted-foreground">
                              Updates context only
                            </span>
                          </div>
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  {actionType === "webhook_creation" && (
                    <p className="text-xs text-muted-foreground">
                      Webhook actions must run in foreground mode
                    </p>
                  )}
                </div>
                {actionType === "api_call" && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-gc-med text-foreground">
                        Response Type
                      </Label>
                      <Tooltip content="Choose whether to make real API calls or return mock responses">
                        <Info className="w-4 h-4 text-muted-foreground" />
                      </Tooltip>
                    </div>
                    <Select
                      value={actionConfig?.response_type || "real"}
                      onValueChange={(value: "real" | "mock") => {
                        setActionConfig((prev: any) => ({
                          ...prev,
                          response_type: value,
                        }));
                      }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select response type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="real">
                          <div className="flex flex-col">
                            <span className="font-gc-med">Real</span>
                            <span className="text-xs text-muted-foreground">
                              Make actual API calls
                            </span>
                          </div>
                        </SelectItem>
                        <SelectItem value="mock">
                          <div className="flex flex-col">
                            <span className="font-gc-med">Mock</span>
                            <span className="text-xs text-muted-foreground">
                              Return predefined responses
                            </span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="space-y-3">
                <Label className="text-sm font-gc-med text-foreground">
                  Description <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what this action does and when it should be used"
                  className="min-h-[100px]"
                />
              </div>
            </div>

            {/* Usage Guidance */}
            <UICard className="border-border">
              <UICardHeader className="pb-4">
                <UICardTitle className="text-xl font-gc-med text-foreground">
                  Usage Guidance
                </UICardTitle>
              </UICardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-gc-med text-foreground">
                        When To Use
                      </Label>
                      <Tooltip content="Situations where this action is appropriate. The planner uses this to select capabilities.">
                        <Info className="w-4 h-4 text-muted-foreground" />
                      </Tooltip>
                    </div>
                    <Textarea
                      value={whenToUse}
                      onChange={(e) => setWhenToUse(e.target.value)}
                      placeholder="e.g., Use when the user asks to send a notification or confirm delivery status."
                      className="min-h-[100px]"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-gc-med text-foreground">
                        When Not To Use
                      </Label>
                      <Tooltip content="Situations where this action should be avoided.">
                        <Info className="w-4 h-4 text-muted-foreground" />
                      </Tooltip>
                    </div>
                    <Textarea
                      value={whenNotToUse}
                      onChange={(e) => setWhenNotToUse(e.target.value)}
                      placeholder="e.g., Do not use for billing issues or account deletion."
                      className="min-h-[100px]"
                    />
                  </div>
                </div>
              </CardContent>
            </UICard>

            {/* Type-Specific Configuration Section */}
            <div className="space-y-6">
              {actionType === "api_call" ? (
                <div className="border-t pt-6">
                  <AgentActionAPIEdit
                    config={actionConfig}
                    onChange={setActionConfig}
                    actionName={actionName}
                    description={description}
                    onActionNameChange={setActionName}
                    onDescriptionChange={setDescription}
                  />
                </div>
              ) : actionType === "llm_call" ? (
                <div className="border-t pt-6">
                  <AgentActionLLMEdit
                    ref={llmEditRef}
                    config={actionConfig}
                    onChange={setActionConfig}
                    applicationId={application?.id}
                    references={references}
                    onReferencesChange={setReferences}
                  />
                </div>
              ) : actionType === "webhook_creation" ? (
                <div className="border-t pt-6">
                  <AgentActionWebhookEdit
                    config={actionConfig}
                    onChange={setActionConfig}
                    actionName={actionName}
                    description={description}
                    onActionNameChange={setActionName}
                    onDescriptionChange={setDescription}
                  />
                </div>
              ) : actionType === "maitai_workflow" ? (
                <div className="border-t pt-6">
                  {workflowError && (
                    <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm">
                      {workflowError}
                    </div>
                  )}
                  <AgentActionWorkflowEdit
                    config={actionConfig}
                    onChange={setActionConfig}
                    actionName={actionName}
                    description={description}
                    onActionNameChange={setActionName}
                    onDescriptionChange={setDescription}
                  />
                </div>
              ) : actionType ? (
                <div className="border-t pt-6">
                  <div className="text-center py-8 text-muted-foreground">
                    <p className="text-sm">
                      Unsupported action type: {actionType}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="border-t pt-6">
                  <div className="text-center py-8 text-muted-foreground">
                    <p className="text-sm">
                      Select an action type to configure
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer Section */}
            <div className="flex items-center justify-between pt-6 border-t border-border">
              <div>
                <p className="text-sm text-muted-foreground">
                  Fields marked with <span className="text-red-500">*</span> are
                  required
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={
                    !actionType || !actionName || !description || isSubmitting
                  }
                  variant="outline"
                  className="gap-2 border-maitai-green text-maitai-green hover:bg-maitai-green hover:text-white disabled:border-border disabled:text-muted-foreground disabled:hover:bg-transparent disabled:hover:text-muted-foreground">
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <PlusIcon className="w-4 h-4" />
                      Create Action
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ApplicationActionNew;
